import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  ClipboardPaste,
  Download,
  FileSpreadsheet,
  GitBranch,
  Layers3,
  Link2,
  Loader2,
  Network,
  Sparkles,
  UploadCloud,
} from "lucide-react";
import usePipelineImport from "./usePipelineImport";
import { exportPipelineRows } from "./pipelineImportService";
import {
  PIPELINE_IMPORT_FIELDS,
  summarizePipelineRows,
} from "./pipelineImportUtils";

const SOURCE_OPTIONS = [
  { key: "file", label: "Upload file", detail: "CSV, Excel or TSV", icon: FileSpreadsheet },
  { key: "google", label: "Google Sheet", detail: "Paste a public link", icon: Link2 },
  { key: "paste", label: "Paste rows", detail: "Copy from any sheet", icon: ClipboardPaste },
];

const SAMPLE_CSV = [
  "Stage,Stage Key,Stage Order,Stage Description,Probability,Closed,Active,Node,Node Key,Node Type,Node Order,Node Description,Can Skip,Next Node,Branch Label",
  "New lead,new-lead,1,New inbound enquiries,10,false,true,Verify lead details,verify-details,manual,1,Confirm contact details,false,first-call,Ready",
  "Qualification,qualification,2,Confirm fit and requirements,35,false,true,First call,first-call,decision,1,Understand the requirement,false,send-proposal,Qualified",
  "Proposal,proposal,3,Share the commercial proposal,70,false,true,Send proposal,send-proposal,manual,1,Send the approved proposal,false,close-won,Accepted",
  "Won,won,4,Successfully converted leads,100,true,true,Close won,close-won,manual,1,Confirm the sale,false,,",
].join("\n");

function downloadTemplate() {
  const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "auto-appy-pipeline-template.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function PipelineDetails({ pipeline, onChange }) {
  return (
    <section className="rounded-2xl border border-[#dbe8ef] bg-[#fbfdfe] p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#e5f5fc] text-[#147db5]"><GitBranch className="h-5 w-5" /></span>
        <div>
          <h3 className="text-base font-bold text-[#17384e]">Pipeline details</h3>
          <p className="text-sm text-[#6d8798]">Name the pipeline that this structure will create.</p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <label>
          <span className="text-sm font-bold text-[#294d63]">Pipeline name <span className="text-red-500">*</span></span>
          <input
            type="text"
            value={pipeline.name}
            onChange={(event) => onChange("name", event.target.value)}
            placeholder="e.g. Sales and onboarding"
            className="mt-2 w-full rounded-xl border border-[#cfe1eb] bg-white px-4 py-3 text-sm text-[#17384e] outline-none transition placeholder:text-[#9aafbd] focus:border-[#49add8] focus:ring-4 focus:ring-[#dff3fb]"
          />
        </label>
        <label>
          <span className="text-sm font-bold text-[#294d63]">Description</span>
          <input
            type="text"
            value={pipeline.description}
            onChange={(event) => onChange("description", event.target.value)}
            placeholder="What this pipeline is used for"
            className="mt-2 w-full rounded-xl border border-[#cfe1eb] bg-white px-4 py-3 text-sm text-[#17384e] outline-none transition placeholder:text-[#9aafbd] focus:border-[#49add8] focus:ring-4 focus:ring-[#dff3fb]"
          />
        </label>
      </div>

      <label className="mt-4 flex cursor-pointer items-center gap-3 rounded-xl border border-[#dce9f0] bg-white px-4 py-3">
        <input
          type="checkbox"
          checked={pipeline.is_default}
          onChange={(event) => onChange("is_default", event.target.checked)}
          className="h-4 w-4 accent-[#147db5]"
        />
        <span>
          <span className="block text-sm font-bold text-[#294d63]">Make this the default pipeline</span>
          <span className="block text-xs text-[#7892a4]">New CRM work will use this pipeline first.</span>
        </span>
      </label>
    </section>
  );
}

function SourcePicker({ loading, loadFile, loadGoogleSheet, loadPaste, editing = false }) {
  const [source, setSource] = useState("file");
  const [sheetUrl, setSheetUrl] = useState("");
  const [pastedRows, setPastedRows] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef(null);

  return (
    <section className="rounded-2xl border border-[#dbe8ef] bg-white p-5">
      <div className="flex items-center gap-2 text-[#1689c1]">
        <Sparkles className="h-4 w-4" />
        <span className="text-xs font-bold uppercase tracking-[0.14em]">{editing ? "Step 2" : "Import structure"}</span>
      </div>
      <h3 className="mt-2 text-xl font-bold tracking-tight text-[#102f44]">{editing ? "Bring back your edited pipeline" : "Where is your pipeline plan?"}</h3>
      <p className="mt-1 text-sm leading-6 text-[#637e91]">{editing ? "Upload the downloaded CSV, paste its rows, or paste the public Google Sheet link." : "Use one row per workflow node. Repeat a stage name when it contains multiple nodes."}</p>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {SOURCE_OPTIONS.map((option) => {
          const Icon = option.icon;
          const selected = source === option.key;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => setSource(option.key)}
              className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition ${selected ? "border-[#49add8] bg-[#eef9fd]" : "border-[#dbe8ef] hover:border-[#a8cede] hover:bg-[#fbfdfe]"}`}
            >
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${selected ? "bg-[#1689c1] text-white" : "bg-[#edf5f8] text-[#58788d]"}`}><Icon className="h-5 w-5" /></span>
              <span><span className="block text-sm font-bold text-[#17384e]">{option.label}</span><span className="block text-xs text-[#7892a4]">{option.detail}</span></span>
            </button>
          );
        })}
      </div>

      {source === "file" && (
        <div className="mt-5">
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              if (event.dataTransfer.files?.[0]) loadFile(event.dataTransfer.files[0]);
            }}
            disabled={loading}
            className={`flex w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-9 text-center transition ${dragging ? "border-[#1689c1] bg-[#eef9fd]" : "border-[#cfe1eb] bg-[#fbfdfe] hover:border-[#72bad8]"}`}
          >
            {loading ? <Loader2 className="h-7 w-7 animate-spin text-[#1689c1]" /> : <UploadCloud className="h-7 w-7 text-[#1689c1]" />}
            <span className="mt-3 text-sm font-bold text-[#17384e]">{loading ? "Reading structure…" : "Drop a file here or choose from your computer"}</span>
            <span className="mt-1 text-xs text-[#7892a4]">CSV, XLSX, XLS, TSV or ODS · up to 1,000 rows</span>
          </button>
          <input ref={fileInput} type="file" accept=".csv,.xlsx,.xls,.tsv,.ods,.xlsb,.txt,text/csv" className="hidden" onChange={(event) => { if (event.target.files?.[0]) loadFile(event.target.files[0]); event.target.value = ""; }} />
          {!editing && <button type="button" onClick={downloadTemplate} className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#147db5] hover:text-[#0d5f8e]"><Download className="h-4 w-4" /> Download pipeline template</button>}
        </div>
      )}

      {source === "google" && (
        <div className="mt-5 rounded-2xl border border-[#dbe8ef] bg-[#fbfdfe] p-5">
          <label htmlFor="pipeline-google-sheet" className="text-sm font-bold text-[#17384e]">Public Google Sheet link</label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <input id="pipeline-google-sheet" type="url" value={sheetUrl} onChange={(event) => setSheetUrl(event.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" className="min-w-0 flex-1 rounded-xl border border-[#cfe1eb] bg-white px-4 py-3 text-sm text-[#17384e] outline-none focus:border-[#49add8] focus:ring-4 focus:ring-[#dff3fb]" />
            <button type="button" onClick={() => loadGoogleSheet(sheetUrl)} disabled={loading || !sheetUrl.trim()} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#147db5] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#106a99] disabled:opacity-50">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Load sheet</button>
          </div>
          <p className="mt-3 text-sm leading-6 text-[#637e91]">In Google Sheets choose <strong>Share</strong>, set General access to <strong>Anyone with the link</strong>, then paste the link. The selected tab is respected.</p>
        </div>
      )}

      {source === "paste" && (
        <div className="mt-5">
          <label htmlFor="pipeline-pasted-rows" className="text-sm font-bold text-[#17384e]">Paste spreadsheet rows</label>
          <textarea id="pipeline-pasted-rows" value={pastedRows} onChange={(event) => setPastedRows(event.target.value)} rows={7} placeholder={"Stage\tStage Order\tNode\tNode Key\tNode Type\tNext Node\nNew lead\t1\tVerify details\tverify\tmanual\tfirst-call\nQualification\t2\tFirst call\tfirst-call\tdecision\t"} className="mt-2 w-full resize-none rounded-2xl border border-[#cfe1eb] bg-[#fbfdfe] px-4 py-3 font-mono text-sm leading-6 text-[#17384e] outline-none focus:border-[#49add8] focus:bg-white focus:ring-4 focus:ring-[#dff3fb]" />
          <div className="mt-3 flex justify-end"><button type="button" onClick={() => loadPaste(pastedRows)} disabled={!pastedRows.trim()} className="inline-flex items-center gap-2 rounded-xl bg-[#147db5] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#106a99] disabled:opacity-50">Map structure <ArrowRight className="h-4 w-4" /></button></div>
        </div>
      )}
    </section>
  );
}

function StructureMapping({ importer }) {
  const mappedColumns = useMemo(() => new Set(Object.values(importer.mapping).filter(Boolean)), [importer.mapping]);
  const summary = useMemo(() => summarizePipelineRows(importer.rows, importer.mapping), [importer.mapping, importer.rows]);
  const previewColumns = importer.columns.slice(0, 6);

  return (
    <section className="rounded-2xl border border-[#dbe8ef] bg-white p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#1689c1]">Map pipeline structure</p>
          <h3 className="mt-2 text-xl font-bold text-[#102f44]">Match sheet columns to workflow fields</h3>
          <p className="mt-1 text-sm text-[#637e91]">Stage name is required. Node key is recommended when your workflow branches.</p>
        </div>
        <button type="button" onClick={importer.resetSource} className="inline-flex items-center gap-2 self-start rounded-xl px-3 py-2 text-sm font-bold text-[#58788d] hover:bg-[#edf5f8]"><ArrowLeft className="h-4 w-4" /> Change source</button>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3">
        {[
          { label: "Stages", value: summary.stages, icon: Layers3 },
          { label: "Nodes", value: summary.nodes, icon: GitBranch },
          { label: "Connections", value: summary.connections, icon: Network },
        ].map((item) => {
          const Icon = item.icon;
          return <div key={item.label} className="rounded-xl border border-[#dce9f0] bg-[#f8fcfe] p-3"><Icon className="h-4 w-4 text-[#1689c1]" /><p className="mt-2 text-xl font-bold text-[#17384e]">{item.value}</p><p className="text-xs font-semibold text-[#7892a4]">{item.label}</p></div>;
        })}
      </div>

      <div className="mt-4 rounded-xl border border-[#cfe8f3] bg-[#f2faff] px-4 py-3 text-sm leading-6 text-[#45657a]">
        <strong>{importer.totalRows.toLocaleString()} rows</strong> from {importer.sourceLabel}.
        {summary.automaticConnections ? " Next node is not mapped, so nodes will connect in sheet order." : " Connections follow the mapped Next node column."}
        {importer.truncated && " Only the first 1,000 rows will be imported."}
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-2">
        {PIPELINE_IMPORT_FIELDS.map((field) => {
          const missing = field.required && !importer.mapping[field.key];
          return (
            <label key={field.key} className={`rounded-xl border p-3 ${missing ? "border-[#f2b8b5] bg-[#fffafa]" : "border-[#dbe8ef] bg-white"}`}>
              <span className="flex items-center justify-between gap-3"><span className="text-sm font-bold text-[#17384e]">{field.label}{field.required && <span className="ml-1 text-red-500">*</span>}</span><span className="text-[11px] font-bold uppercase tracking-wide text-[#8aa0af]">{field.group}</span></span>
              <select value={importer.mapping[field.key] || ""} onChange={(event) => importer.setFieldMapping(field.key, event.target.value)} className="mt-2 w-full rounded-lg border border-[#cfe1eb] bg-[#fbfdfe] px-3 py-2.5 text-sm font-medium text-[#34566d] outline-none focus:border-[#49add8]">
                <option value="">Do not import</option>
                {importer.columns.map((column) => <option key={column} value={column} disabled={mappedColumns.has(column) && importer.mapping[field.key] !== column}>{column}</option>)}
              </select>
            </label>
          );
        })}
      </div>

      <div className="mt-5 max-w-full overflow-x-auto rounded-xl border border-[#dbe8ef]">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-[#f3f8fb] text-[#45657a]"><tr>{previewColumns.map((column) => <th key={column} className="whitespace-nowrap px-4 py-3 font-bold">{column}</th>)}</tr></thead>
          <tbody className="divide-y divide-[#e5eef3]">{importer.rows.slice(0, 4).map((row, index) => <tr key={index}>{previewColumns.map((column) => <td key={column} className="max-w-56 truncate whitespace-nowrap px-4 py-3 text-[#516f82]">{row[column] || "—"}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </section>
  );
}

export default function PipelineImportPanel({ pipeline, onChange, onCancel, onImported, mode = "create", hideDetails = false, onBusyChange }) {
  const editing = mode === "edit";
  const importer = usePipelineImport({ pipelineId: editing ? pipeline.id : null });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  useEffect(() => { onBusyChange?.(importer.importing); }, [importer.importing, onBusyChange]);

  const handleImport = async () => {
    const result = await importer.runImport(pipeline);
    if (result?.pipeline) onImported(result.pipeline);
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      const response = await exportPipelineRows(pipeline.id);
      const blob = response?.data instanceof Blob
        ? response.data
        : new Blob([response?.data || ""], { type: "text/csv;charset=utf-8" });
      const disposition = response?.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|\")?([^";]+)/i);
      const fallbackName = `${String(pipeline.name || "pipeline").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "pipeline"}-autoappy.csv`;
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1].replace(/"/g, "")) : fallbackName;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(error?.response?.data?.message || "We couldn't download this pipeline.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6 sm:px-8">
        {(importer.error || downloadError) && <div role="alert" className="flex items-start gap-3 rounded-xl border border-[#f2c8c4] bg-[#fff7f6] px-4 py-3 text-sm text-[#963f37]"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{importer.error || downloadError}</span></div>}
        {editing ? (
          <section className="rounded-2xl border border-[#cde4ef] bg-[linear-gradient(135deg,#f1faff_0%,#ffffff_70%)] p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#1689c1] text-white shadow-sm"><Download className="h-5 w-5" /></span>
                <div><p className="text-xs font-bold uppercase tracking-[0.14em] text-[#1689c1]">Step 1</p><h3 className="mt-1 text-lg font-bold text-[#102f44]">Download {pipeline.name}</h3><p className="mt-1 max-w-xl text-sm leading-6 text-[#637e91]">Open the CSV in Google Sheets and make your changes. Keep the Stage Key and Node Key cells unchanged for existing items so live lead history stays connected.</p></div>
              </div>
              <button type="button" onClick={handleDownload} disabled={downloading} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-[#9ccfe4] bg-white px-5 py-3 text-sm font-bold text-[#126f9f] shadow-sm transition hover:border-[#49add8] hover:bg-[#f5fbfe] disabled:opacity-60">{downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}{downloading ? "Preparing…" : "Download CSV"}</button>
            </div>
          </section>
        ) : !hideDetails && <PipelineDetails pipeline={pipeline} onChange={onChange} />}
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm leading-6 text-[#17384e]"><strong>Workflow import only.</strong> This sheet must describe stages, actions and connections—not people or lead columns. {hideDetails ? "Use the Lead table tab for a spreadsheet of leads or a JSON table schema. Your table draft is included when this pipeline is created." : "Configure lead columns separately in Lead table setup."}</div>
        {importer.rows.length === 0
          ? <SourcePicker editing={editing} loading={importer.loading} loadFile={importer.loadFile} loadGoogleSheet={importer.loadGoogleSheet} loadPaste={importer.loadPaste} />
          : <StructureMapping importer={importer} />}
      </div>

      <footer className="flex items-center justify-end gap-3 border-t border-[#dcebf3] bg-[#fbfdfe] px-6 py-4 sm:px-8">
        <button type="button" onClick={onCancel} disabled={importer.importing} className="rounded-xl px-4 py-2.5 text-sm font-bold text-[#58788d] hover:bg-[#edf5f8]">Cancel</button>
        {importer.rows.length > 0 && <button type="button" onClick={handleImport} disabled={importer.importing || (!editing && !pipeline.name.trim()) || !importer.mapping.stage_name} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-[#1689c1] to-[#106a99] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50">{importer.importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}{importer.importing ? (editing ? "Updating pipeline…" : "Creating pipeline…") : (editing ? "Update pipeline" : "Create imported pipeline")}</button>}
      </footer>
    </div>
  );
}
